import { LitElement, html, css } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { nsSharedStyles, nsAnimationStyles, nsLayoutStyles } from '../ns-styles/index.js';
import { createLogger } from './logger.js';
import { TOAST_SHOWN_EVENT } from '../helpers/toast-events.js';

const logger = createLogger('ns-loader');

@customElement('ns-loader')
export class NSLoader extends LitElement {
  private __isLoading: boolean = false;

  private __spinningAnimation?: Animation;

  private __message?: string;

  // startClosed was called before the first render: applied once there is something to close
  private __pendingClosed?: { message?: string };

  @query('#message')
  private __messageElement?: HTMLElement;

  static styles = [
    nsSharedStyles,
    nsAnimationStyles,
    nsLayoutStyles,
    css`
      /* Pinned to the window: it covers exactly the screen and is never part of the page's layout, so it can't be pushed
         down by content rendering under it and stretch the page into a scrollbar */
      :host {
        position: fixed;
        inset: 0;

        height: 100vh;
        width: 100%;

        overflow: hidden;

        z-index: 9999;

        display: block;
        color: var(--ns-theme-white);

        /* While it shows it is a popover in the top layer (__toTopLayer), so the browser's popover box styles are undone */
        margin: 0;
        padding: 0;
        border: 0;
        max-width: none;
        max-height: none;
        background: transparent;
      }

      :host(.hide) {
        display: none;
      }

      .loading-message-wrapper {
        position: relative;
        display: flex;
      }

      .loading-animation-wrapper img {
        display: block;
      }

      #message {
        position: absolute;
        top: 150px;
        left: 50%;
        transform: translateX(-50%);
        width: max-content;
        color: var(--ns-theme-grey-mid);
        max-width: 500px;
        text-align: center;
        opacity: 1;
      }
      #message.hide {
        opacity: 0;
      }
    `,
  ];

  private __getAppContainer(): HTMLElement | null {
    const root = this.getRootNode() as Document | ShadowRoot;
    return root.querySelector('#app-container');
  }

  /**
   * Shows the loader already closed, the logo spinning, with no closing animation: for a page load, so the first thing on
   * screen is the closed shutter. Called before the loader has rendered, it closes it on its first render.
   * finishLoading opens it as usual.
   */
  startClosed(message?: string) {
    const loadingAnimation = this.renderRoot?.querySelector('.loading-animation') as HTMLElement | null;
    if (!loadingAnimation) {
      this.__pendingClosed = { message };
      return;
    }

    if (this.__isLoading) {
      this.updateMessage(message);
      return;
    }

    this.classList.remove('hide');
    this.__toTopLayer();
    this.__getAppContainer()?.toggleAttribute('inert', true);
    loadingAnimation.classList.add('collapse', 'collapsed', 'started');
    this.__showMessage();
    this.__startSpinning();

    this.__isLoading = true;
    this.updateMessage(message);
  }

  // Toasts open in the top layer (wa-toast is a popover), above any z-index; while the shutter shows it is a popover too,
  // and lifts itself back on top whenever a toast opens over it
  private __onToastShown = () => {
    if (this.classList.contains('hide')) return;
    // After the toast's own popover has opened (it opens as the item is added); a timer also runs in a background tab
    setTimeout(() => this.__toTopLayer(), 0);
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(TOAST_SHOWN_EVENT, this.__onToastShown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(TOAST_SHOWN_EVENT, this.__onToastShown);
  }

  // Into the top layer, last in it, so it covers every other popover (a later one goes above it until __onToastShown lifts it)
  private __toTopLayer() {
    if (typeof this.showPopover !== 'function') return;
    try {
      if (!this.hasAttribute('popover')) this.setAttribute('popover', 'manual');
      if (this.matches(':popover-open')) this.hidePopover();
      this.showPopover();
    } catch {
      // A browser without popovers keeps the z-index
    }
  }

  private __leaveTopLayer() {
    try {
      if (this.matches(':popover-open')) this.hidePopover();
    } catch {
      // As above
    }
    this.removeAttribute('popover');
  }

  protected firstUpdated() {
    if (!this.__pendingClosed) return;
    const { message } = this.__pendingClosed;
    this.__pendingClosed = undefined;
    this.startClosed(message);
  }

  private __startSpinning() {
    const logoWrapper = this.renderRoot.querySelector('.loading-animation-wrapper') as HTMLElement;
    this.__spinningAnimation = logoWrapper.animate(
      [
        {
          transform: 'rotate(0deg)',
        },
        {
          transform: 'rotate(360deg)',
        },
      ],
      {
        duration: 1000,
        easing: 'linear',
        fill: 'forwards',
        iterations: Infinity,
      },
    );
  }

  async startLoading(animationFunction?: string, message?: string) {
    const loadingAnimation = this.renderRoot.querySelector('.loading-animation') as HTMLElement;
    if (!loadingAnimation) return;

    if (this.__isLoading) {
      this.updateMessage(message);
      return;
    }

    this.classList.remove('hide');
    this.__toTopLayer();
    this.__getAppContainer()?.toggleAttribute('inert', true);

    loadingAnimation.classList.add('collapse');
    const upperSide = this.renderRoot.querySelector('.upper-side') as HTMLElement;
    const bottomSide = this.renderRoot.querySelector('.bottom-side') as HTMLElement;

    const upperSideAnimation = upperSide.animate(
      [
        {
          transform: 'translate(20%, -100%)',
        },
        {
          transform: 'translate(20%, 0)',
        },
      ],
      {
        duration: 500,
        easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
      },
    );
    const bottomSideAnimation = bottomSide.animate(
      [
        {
          transform: 'translate(-20%, 100%)',
        },
        {
          transform: 'translate(-20%, 0)',
        },
      ],
      {
        duration: 500,
        easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
      },
    );

    await upperSideAnimation.finished;
    await bottomSideAnimation.finished;
    loadingAnimation.classList.add('collapsed');

    this.__showMessage();

    const leftLogo = this.renderRoot.querySelector('.loading-animation-wrapper .logo-upward img') as HTMLElement;
    const rightLogo = this.renderRoot.querySelector('.loading-animation-wrapper .logo-downward img') as HTMLElement;

    const opacityAnimation = [
      {
        opacity: 0,
      },
      {
        opacity: 1,
      },
    ];
    const opacityArgs = {
      duration: 500,
      easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
    };
    const leftLogoAnimation = leftLogo.animate(opacityAnimation, opacityArgs);
    const rightLogoAnimation = rightLogo.animate(opacityAnimation, opacityArgs);

    await leftLogoAnimation.finished;
    await rightLogoAnimation.finished;
    loadingAnimation.classList.add('started');
    this.__startSpinning();

    this.__isLoading = true;
    this.updateMessage(message);
  }

  async finishLoading(animationFunction?: string) {
    // Asked to open before a pending startClosed has run (the loader hadn't rendered): close it first, then open
    if (this.__pendingClosed) await this.updateComplete;
    const loadingAnimation = this.renderRoot.querySelector('.loading-animation') as HTMLElement;
    if (!loadingAnimation || !this.__spinningAnimation) return;

    await this.__cancelAnimation();

    const leftLogo = this.renderRoot.querySelector('.loading-animation-wrapper .logo-upward img') as HTMLElement;
    const rightLogo = this.renderRoot.querySelector('.loading-animation-wrapper .logo-downward img') as HTMLElement;

    const opacityAnimation = [
      {
        opacity: 1,
      },
      {
        opacity: 0,
      },
    ];
    const opacityArgs = {
      duration: 500,
      easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
    };
    const leftLogoAnimation = leftLogo.animate(opacityAnimation, opacityArgs);
    const rightLogoAnimation = rightLogo.animate(opacityAnimation, opacityArgs);
    leftLogoAnimation.pause();
    rightLogoAnimation.pause();

    leftLogoAnimation.play();
    rightLogoAnimation.play();
    await leftLogoAnimation.finished;
    await rightLogoAnimation.finished;
    loadingAnimation.classList.add('hide-logo');

    this.__hideMessage();
    const upperSide = this.renderRoot.querySelector('.upper-side') as HTMLElement;
    const bottomSide = this.renderRoot.querySelector('.bottom-side') as HTMLElement;

    const upperSideAnimation = upperSide.animate(
      [
        {
          transform: 'translate(20%, 0)',
        },
        {
          transform: 'translate(20%, -100%)',
        },
      ],
      {
        duration: 500,
        easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
      },
    );
    const bottomSideAnimation = bottomSide.animate(
      [
        {
          transform: 'translate(-20%, 0)',
        },
        {
          transform: 'translate(-20%, 100%)',
        },
      ],
      {
        duration: 500,
        easing: animationFunction ? animationFunction : 'cubic-bezier(0.43, 0.12, 0.39, 0.96)',
      },
    );

    await upperSideAnimation.finished;
    await bottomSideAnimation.finished;

    loadingAnimation.classList.remove(...['collapsed', 'hide-logo', 'collapse', 'started']);
    this.classList.add('hide');
    this.__leaveTopLayer();

    this.__isLoading = false;
    this.__getAppContainer()?.toggleAttribute('inert', false);
  }

  private async __showMessage() {
    if (!this.__messageElement) return logger.error('Missing loading message element');

    this.__messageElement.classList.remove('hide');
  }

  private async __hideMessage() {
    if (!this.__messageElement) return logger.error('Missing loading message element');

    this.__messageElement.classList.add('hide');
  }

  private async __cancelAnimation() {
    if (!this.__spinningAnimation || !this.__spinningAnimation.effect) {
      throw new Error('Could not find spinning animation');
    }

    const duration = this.__spinningAnimation.effect.getTiming().duration as number;
    const currentTime = this.__spinningAnimation.currentTime as number;
    const timeRemaining = duration - currentTime;
    await new Promise((resolve) => setTimeout(resolve, timeRemaining));
    this.__spinningAnimation.cancel();
  }

  get isLoading() {
    return this.__isLoading;
  }

  updateMessage(message?: string) {
    this.__message = message;
    this.requestUpdate();
  }

  render() {
    return html`
      <div class="vertical center-center w-100 h-100 loading-animation" role="status" aria-live="polite">
        <div class="w-100 h-100 animation-background" aria-hidden="true">
          <div class="w-100 upper-side"></div>
          <div class="w-100 bottom-side"></div>
        </div>
        <div class="loading-message-wrapper">
          <div class="layout inline loading-animation-wrapper" aria-hidden="true">
            <div class="logo-upward">
              <img src="/images/animation-asset/upward-arrow.svg" alt="" />
            </div>
            <div class="logo-downward">
              <img src="/images/animation-asset/downward-arrow.svg" alt="" />
            </div>
          </div>
          <div id="message" class="hide">${this.__message ? this.__message : 'Loading, please wait...'}</div>
        </div>
      </div>
    `;
  }
}
